import { OctagonX, RefreshCw, TriangleAlert, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { cn, errorMessage } from '../../lib/utils';
import { Button } from '../ui/button';

export interface ProblemBannerProps {
  /** Anything thrown by a hook: `ApiError` (problem+json), `Error`, or a string. `null` renders nothing. */
  error: unknown;
  /** Overrides the problem title. */
  title?: string;
  /** Field errors already shown next to their inputs; they are left out of the list here. */
  omitPaths?: ReadonlySet<string> | readonly string[];
  onRetry?: () => void;
  /** Extra content (e.g. a "reload" action) rendered under the message. */
  children?: ReactNode;
  className?: string;
  /** Compact variant for dialogs and cards. */
  size?: 'default' | 'sm';
}

/**
 * Renders an RFC 9457 problem (`title`, `detail`, `code`, `errors[]`) or any other error as an
 * alert. Used at the top of pages, dialogs and `SchemaForm`.
 */
export function ProblemBanner({
  error,
  title,
  omitPaths,
  onRetry,
  children,
  className,
  size = 'default',
}: ProblemBannerProps) {
  if (error === null || error === undefined) return null;
  const view = describe(error);
  const omitted = omitPaths instanceof Set ? omitPaths : new Set(omitPaths ?? []);
  const fieldErrors = view.fieldErrors.filter((f) => !omitted.has(f.path));
  const Icon =
    view.kind === 'network' ? WifiOff : view.kind === 'warning' ? TriangleAlert : OctagonX;

  return (
    <div
      role="alert"
      data-slot="problem-banner"
      data-status={view.status ?? undefined}
      data-code={view.code ?? undefined}
      className={cn(
        'flex gap-3 rounded-md border text-sm',
        view.kind === 'warning'
          ? 'border-warning/30 bg-warning-soft text-warning'
          : 'border-danger/30 bg-danger-soft text-danger',
        size === 'sm' ? 'p-3' : 'p-4',
        className,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-medium">{title ?? view.title}</p>
        {view.detail && view.detail !== view.title ? (
          <p className="text-[13px] opacity-90">{view.detail}</p>
        ) : null}
        {fieldErrors.length > 0 ? (
          <ul className="list-disc space-y-0.5 pl-5 text-[13px]">
            {fieldErrors.map((f) => (
              <li key={`${f.path}:${f.message}`}>
                <code className="rounded bg-surface-raised/60 px-1 font-mono text-xs">
                  {f.path}
                </code>{' '}
                {f.message}
              </li>
            ))}
          </ul>
        ) : null}
        {view.code ? (
          <p className="font-mono text-xs opacity-70">
            {view.code}
            {view.status ? ` · HTTP ${view.status}` : ''}
          </p>
        ) : null}
        {children}
      </div>
      {onRetry ? (
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          className="shrink-0 bg-surface-raised"
        >
          <RefreshCw />
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export interface ProblemView {
  kind: 'error' | 'warning' | 'network';
  title: string;
  detail: string | null;
  code: string | null;
  status: number | null;
  fieldErrors: ReadonlyArray<{ path: string; message: string }>;
}

/** Normalizes any thrown value into what the banner (or a toast) shows. */
export function describe(error: unknown): ProblemView {
  if (error instanceof ApiError) {
    if (error.isNetworkError) {
      return {
        kind: 'network',
        title: 'Cannot reach the API',
        detail: error.message,
        code: null,
        status: null,
        fieldErrors: [],
      };
    }
    const problem = error.problem;
    const isConflict = error.status === 409 || error.status === 412;
    return {
      kind: isConflict ? 'warning' : 'error',
      title: problem?.title ?? defaultTitle(error.status),
      detail: problem?.detail ?? (problem?.title ? null : error.message),
      code: error.code,
      status: error.status,
      fieldErrors: error.fieldErrors,
    };
  }
  return {
    kind: 'error',
    title: 'Something went wrong',
    detail: errorMessage(error),
    code: null,
    status: null,
    fieldErrors: [],
  };
}

function defaultTitle(status: number): string {
  switch (status) {
    case 400:
      return 'Validation failed';
    case 401:
      return 'Your session has expired';
    case 403:
      return 'You are not allowed to do this';
    case 404:
      return 'Not found';
    case 409:
      return 'Conflict';
    case 412:
      return 'This record changed since you loaded it';
    case 413:
      return 'Payload too large';
    case 429:
      return 'Too many requests';
    default:
      return status >= 500 ? 'The server had a problem' : 'Request failed';
  }
}
